import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { useAuth } from '../App';
import toast from 'react-hot-toast';
import { AlertTriangle, CheckCircle, Clock, Plus, RefreshCw, Trash2, Upload, XCircle } from 'lucide-react';
import { deriveVehicleKonzession, deriveVehicleZulassung } from '../lib/vehicleKonzession';
import { nurDatum } from '../lib/zeit';
import PageHeader from '../components/PageHeader';
import VehicleAssignments from '../components/VehicleAssignments';
import VehicleDraftWizard from '../components/VehicleDraftWizard';   // DOKUMENT-ERKENNUNG-PARTNER-001: Entwurf → Uploads → Felder → Freigabe

const statusBadge = {
  approved: { label: 'Genehmigt', color: '#9C27B0', bg: '#faf5fb' },
  pending:  { label: 'In Prüfung', color: '#4b5563', bg: '#f3f4f6' },
  rejected: { label: 'Abgelehnt',  color: '#dc2626', bg: '#fef2f2' },
  suspended: { label: 'Gesperrt',  color: '#b45309', bg: '#fffbeb' },   // ADMIN-FAHRZEUG-PARTNER-SPERRE-001: vom Admin gesperrt
  draft:    { label: 'Entwurf',    color: '#9C27B0', bg: '#faf5fb' },
};

const SEAT_TYPES = [
  { seats: 4, label: 'Basis · Komfort · Business', desc: 'VOOGO Basis · VOOGO Komfort · VOOGO Business', color: '#4b5563' },
  { seats: 6, label: 'Family',                            desc: 'VOOGO Family (bis 6 Pers.)',                  color: '#4b5563' },
  { seats: 7, label: 'Van',                     desc: 'VOOGO Van (bis 7+ Pers.)',                color: '#9C27B0' },
];

// DOC-LIFECYCLE-001R1: Icon + Upload-Beschriftung je Konzessions-Zustand (Ableitung siehe lib/vehicleKonzession).
const konzIcon = (state) => {
  if (state === 'approved') return <CheckCircle size={13} color="#9C27B0" />;
  if (state === 'renewalPending') return <RefreshCw size={13} color="#4b5563" />;
  if (state === 'pending') return <Clock size={13} color="#4b5563" />;
  if (state === 'rejected') return <XCircle size={13} color="#dc2626" />;
  if (state === 'expired') return <AlertTriangle size={13} color="#dc2626" />;
  return <Upload size={13} color="#6b7280" />;
};
const konzUploadLabel = (state) => {
  if (state === 'expired') return 'Konzession erneuern';
  if (state === 'rejected') return 'Konzession neu einreichen';
  if (state === 'approved') return 'Konzession erneuern';
  return 'Konzession hochladen';
};
const zulassungUploadLabel = (state) => {
  if (state === 'rejected') return 'Zulassungsschein neu einreichen';
  if (state === 'approved') return 'Zulassungsschein aktualisieren';
  return 'Zulassungsschein hochladen';
};

// Anzeige-Labels der automatisch zugewiesenen Klasse.
const CLASS_LABELS = { standard: 'VOOGO Basis', comfort: 'VOOGO Komfort', premium: 'VOOGO Business', xl: 'VOOGO Family', max: 'VOOGO Van', taxi: 'VOOGO Taxi' };
// Welche Fahrt-Kategorien ein Fahrzeug anbietet (analog Backend allowedRideTypes).
const offeredTypes = (v) => {
  const t = ['VOOGO Basis', 'VOOGO Komfort'];
  if (v.vehicle_class === 'premium') t.push('VOOGO Business');
  if ((v.seats || 4) >= 6) t.push('VOOGO Family');
  if ((v.seats || 4) >= 7) t.push('VOOGO Van');
  return t;
};

export default function Vehicles() {
  const { partner } = useAuth();
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [fileId, setFileId] = useState(null);
  const [removingVehicle, setRemovingVehicle] = useState(null);
  const [removeBusy, setRemoveBusy] = useState(false);
  const [loading, setLoading] = useState(false);
  // DOC-LIFECYCLE-001R1: Ausstellungs-/Ablaufdatum je Fahrzeug für die Konzessions-ERNEUERUNG.
  const [konzDates, setKonzDates] = useState({});   // { [vehicleId]: { issue_date, expires_at } }
  // FAHRZEUG-ZULASSUNG-001: Zulassungsschein hat nur ein Ausstellungsdatum, nie ein Ablaufdatum.
  const [zulassungDates, setZulassungDates] = useState({});

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const [fleet, people] = await Promise.all([api.get('/partners/vehicles'), api.get('/partners/drivers')]);
      const rows = fleet.data.vehicles || [];
      setVehicles(rows);
      setFileId(current => current && !rows.some(v => v.id === current) ? null : current);
      setDrivers(people.data.drivers || []);
    } catch (_) { toast.error('Fuhrpark konnte nicht geladen werden. Bitte erneut versuchen.'); }
  };

  // DOKUMENT-ERKENNUNG-PARTNER-001: „Fahrzeug hinzufügen" legt einen ENTWURF an. Dokumente,
  // Erkennung und Bestätigung laufen in der Entwurfskarte (VehicleDraftWizard); erst die
  // Bestätigung schickt das Fahrzeug in die Admin-Prüfung.
  const addVehicle = async () => {
    if (partner?.approval_status !== 'approved') return toast.error('Konto muss zuerst freigegeben sein');
    setLoading(true);
    try {
      const result = await api.post('/partners/vehicles/draft', {});
      if (result.data.vehicle?.id) setFileId(result.data.vehicle.id);
      toast.success('Entwurf angelegt — lade jetzt Fahrzeugschein und Konzession hoch');
      load();
    } catch (err) { toast.error(err.response?.data?.error || 'Fehler'); }
    finally { setLoading(false); }
  };

  // DOC-LIFECYCLE-001R1: Ausstellungs-/Ablaufdatum je Fahrzeug — Vorbelegung aus den Fahrzeugdaten,
  // überschreibbar für die Erneuerung (neue Gültigkeit). Die Konzession verlangt BEIDE Daten.
  const konzDatesFor = (v) => konzDates[v.id] || { issue_date: v.konzession_issue || '', expires_at: v.konzession_expiry || '' };
  const setKonzDate = (v, field, value) => setKonzDates((s) => ({ ...s, [v.id]: { ...konzDatesFor(v), [field]: value } }));

  const zulassungDatesFor = (v) => {
    const current = deriveVehicleZulassung(v);
    return zulassungDates[v.id] || { issue_date: current.issuedAt || '' };
  };
  const setZulassungIssue = (v, value) => setZulassungDates((s) => ({ ...s, [v.id]: { issue_date: value } }));

  const uploadVehicleDocument = async (vehicle, documentType, dates, successMessage, e) => {
    const file = e.target.files[0];
    if (!file) { e.target.value = ''; return; }
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('document_type', documentType);
      fd.append('issue_date', dates.issue_date || '');
      // Die Zulassung besitzt dieses Feld nicht; ausschließlich die Konzession reicht
      // expires_at ein. So bleibt deren bestehende FormData-Struktur unverändert.
      if (documentType === 'konzession') fd.append('expires_at', dates.expires_at || '');
      await api.post(`/partners/vehicles/${vehicle.id}/documents`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(successMessage);
      load();
    } catch (e2) { toast.error(e2.response?.data?.error || 'Upload fehlgeschlagen'); }
    e.target.value = '';
  };

  // Konzession hochladen/erneuern. Der Erneuerungspfad bleibt AUCH bei gesperrtem (abgelaufenem/
  // abgelehntem) Fahrzeug offen — genau damit die abgelaufene Urkunde nachreichbar ist.
  const uploadKonzession = async (vehicle, e) => {
    const d = konzDatesFor(vehicle);
    await uploadVehicleDocument(vehicle, 'konzession', d, 'Konzession eingereicht – sie wird geprüft', e);
  };

  const uploadZulassung = async (vehicle, e) => {
    const d = zulassungDatesFor(vehicle);
    await uploadVehicleDocument(vehicle, 'zulassung', d, 'Zulassungsschein eingereicht – er wird geprüft', e);
  };

  const removeVehicle = async (vehicle) => {
    setRemoveBusy(true);
    try {
      await api.delete(`/partners/vehicles/${vehicle.id}`);
      toast.success('Fahrzeug entfernt — das Kennzeichen ist wieder frei');
      setRemovingVehicle(null);
      await load();
    } catch (err) {
      const data = err.response?.data || {};
      toast.error(data.message || data.error || 'Fahrzeug konnte nicht entfernt werden');
      if (err.response?.status === 409 && (data.error === 'vehicle_changed' || data.code === 'vehicle_retired')) {
        setRemovingVehicle(null);
        await load();
      }
    } finally {
      setRemoveBusy(false);
    }
  };

  const seatType = (seats) => seats >= 7 ? SEAT_TYPES[2] : seats >= 6 ? SEAT_TYPES[1] : SEAT_TYPES[0];


  return (
    <div>
      <PageHeader
        title={fileId ? "Autoakte" : "Fahrzeuge"}
        description={fileId ? "Fahrzeugdaten und Dokumente" : "Dein Fuhrpark und die zugewiesenen Fahrer"}
        actions={
        <button onClick={addVehicle} disabled={loading} data-testid="vehicle-add"
          style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '9px 16px', background: '#000', color: '#fff', borderRadius: 8, fontSize: 13, fontWeight: 700, opacity: loading ? 0.6 : 1 }}>
          <Plus size={15} /> Fahrzeug hinzufügen
        </button>
        }
      />

      {fileId && <button onClick={() => setFileId(null)} style={{ marginBottom: 20, padding: '10px 14px', borderRadius: 8, background: '#f3f4f6' }}>← Zur Übersicht</button>}

      {/* Vehicle cards */}
      <div style={{ display: 'grid', gridTemplateColumns: fileId ? 'minmax(0, 1fr)' : 'repeat(auto-fill, minmax(300px, 1fr))', maxWidth: fileId ? 820 : undefined, gap: 14 }}>
        {vehicles.length === 0 && (
          <div style={{ background: '#fff', borderRadius: 12, padding: 32, textAlign: 'center', border: '1px solid #e5e7eb', color: '#6b7280', gridColumn: '1/-1' }}>
            Noch keine Fahrzeuge registriert
          </div>
        )}
        {vehicles.filter(v => !fileId || v.id === fileId).sort((a, b) => (a.approval_status === 'draft' ? 0 : 1) - (b.approval_status === 'draft' ? 0 : 1)).map(v => {
          // DOKUMENT-ERKENNUNG-PARTNER-001: Entwürfe stehen oben und haben noch keine Stammdaten — eigene Karte.
          if (v.approval_status === 'draft') return fileId === v.id
            ? <VehicleDraftWizard key={v.id} vehicle={v} onChanged={load} />
            : <div key={v.id} style={{ background: '#fff', padding: 18, borderRadius: 12, border: '1px solid #e5e7eb' }}>
                <h3>Neues Fahrzeug · Entwurf</h3><button onClick={() => setFileId(v.id)}>Autoakte öffnen</button>
              </div>;
          // DOC-LIFECYCLE-001R1: Konzessions-Review-/Ablaufstatus rein aus den Backend-Rohzeilen
          // ableiten (approved/pending/renewalPending/rejected/expired) — inkl. Sperr-Erkennung.
          const kz = deriveVehicleKonzession(v);
          // FAHRZEUG-ZULASSUNG-001: gleicher Review-/Revisionsweg, aber ohne Ablaufdatum und
          // ohne vorweggenommene operative Aktivierung.
          const zs = deriveVehicleZulassung(v);
          const kd = konzDatesFor(v);
          const kDatesOk = !!(kd.issue_date && kd.expires_at);
          const zd = zulassungDatesFor(v);
          const zDateOk = !!zd.issue_date;
          const fmtDate = (d) => (d ? nurDatum(d) : '–');
          const s = statusBadge[v.approval_status] || statusBadge.pending;
          const st = seatType(v.seats || 4);
          // ADMIN-FAHRZEUG-PARTNER-SPERRE-001: Sperrgrund des Admins wie der Ablehnungsgrund anzeigen.
          const rejectionReason = v.approval_status === 'rejected'
            ? String(v.rejection_reason || '').trim()
            : v.approval_status === 'suspended'
              ? `Vom Admin gesperrt — ${String(v.suspended_reason || '').trim() || 'bitte wende dich an den Support'}`
              : '';

          return (
            <div key={v.id} style={{ background: '#fff', borderRadius: 12, padding: 18, border: '1px solid #e5e7eb' }}>
              {/* Header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                <div>
                  <div style={{ fontSize: 16, fontWeight: 800 }}>{v.brand} {v.model}</div>
                  <div style={{ fontSize: 13, color: '#4b5563', marginTop: 2 }}>{v.license_plate}{fileId && v.color ? ` · ${v.color}` : ''}</div>
                  {fileId && <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 12, fontWeight: 800, color: '#4b5563', background: '#f3f4f6', padding: '2px 9px', borderRadius: 6 }}>{CLASS_LABELS[v.vehicle_class] || 'VOOGO Basis'}</span>
                    {v.class_source === 'fallback' && <span style={{ fontSize: 12, fontWeight: 700, color: '#4b5563', background: '#f3f4f6', padding: '2px 7px', borderRadius: 6 }}>Klasse wird vom Admin geprüft</span>}
                  </div>}
                </div>
                <span style={{ fontSize: 12, fontWeight: 700, color: s.color, background: s.bg, padding: '3px 10px', borderRadius: 20 }}>{s.label}</span>
              </div>

              {!fileId && <button data-testid={`vehicle-file-${v.id}`} onClick={() => setFileId(v.id)} style={{ padding: '8px 12px', marginBottom: 14, background: '#faf5fb', color: '#7b1fa2', borderRadius: 8 }}>Autoakte öffnen</button>}
              {fileId && <>
              {rejectionReason && (
                <div data-testid={`vehicle-rejection-reason-${v.id}`}
                  style={{ fontSize: 12, color: '#dc2626', fontWeight: 600, marginBottom: 8 }}>
                  Grund: {rejectionReason}
                </div>
              )}

              {/* Specs */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 12 }}>
                <div style={{ background: '#f9fafb', borderRadius: 8, padding: '8px 10px' }}>
                  <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 2 }}>Baujahr</div>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{v.year || '–'}</div>
                </div>
                <div style={{ background: '#f9fafb', borderRadius: 8, padding: '8px 10px' }}>
                  <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 2 }}>Sitze</div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: st.color }}>{v.seats || 4}</div>
                </div>
              </div>

              {/* Ride types this vehicle can serve */}
              <div style={{ display: 'flex', gap: 4, marginBottom: 12, flexWrap: 'wrap' }}>
                {offeredTypes(v).map(t => (
                  <span key={t} style={{ fontSize: 12, fontWeight: 700, color: '#4b5563', background: '#f3f4f6', padding: '2px 7px', borderRadius: 4 }}>{t}</span>
                ))}
              </div>

              {/* Konzession (§ 49 PBefG) — Review-/Ablaufstatus + Sperr-Kennzeichnung + Erneuerung.
                  Das Portal SPIEGELT nur den Status; die Freigabe entscheidet der Admin. */}
              <div style={{ borderTop: '1px solid #f3f4f6', paddingTop: 12, marginBottom: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#4b5563', textTransform: 'uppercase', letterSpacing: 0.5 }}>Konzession (§ 49 PBefG)</div>
                  <span data-testid={`konz-status-${v.id}`}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 700, color: kz.presentation.color, background: kz.presentation.bg, padding: '3px 9px', borderRadius: 20 }}>
                    {konzIcon(kz.state)} {kz.presentation.label}
                  </span>
                </div>

                {/* Wirksame Freigabe: Gültigkeit sichtbar */}
                {kz.effective && !kz.blocked && (
                  <div style={{ fontSize: 12, color: '#4b5563', marginBottom: kz.renewalOpen ? 4 : 8 }}>
                    Gültig bis {fmtDate(kz.effective.expires_at || kz.expiresAt)}
                  </div>
                )}
                {kz.renewalOpen && (
                  <div style={{ fontSize: 12, color: '#4b5563', marginBottom: 8 }}>Erneuerung eingereicht — wird geprüft.</div>
                )}

                {/* Ablehnungsgrund bleibt persistent sichtbar */}
                {kz.rejectionReason && (
                  <div style={{ fontSize: 12, color: '#dc2626', fontWeight: 600, marginBottom: 8 }}>Grund: {kz.rejectionReason}</div>
                )}

                {/* Sperre: wegen abgelaufener/abgelehnter Konzession KEINE neuen Fahrten */}
                {kz.blocked && (
                  <div data-testid={`konz-block-${v.id}`}
                    style={{ display: 'flex', gap: 8, alignItems: 'flex-start', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '8px 10px', marginBottom: 10 }}>
                    <AlertTriangle size={14} color="#dc2626" style={{ flexShrink: 0, marginTop: 1 }} />
                    <span style={{ fontSize: 12, color: '#991b1b', fontWeight: 600, lineHeight: 1.4 }}>{kz.blockNotice}</span>
                  </div>
                )}

                {/* Erneuerungs-/Upload-Pfad — bleibt AUCH bei Sperre offen; Konzession braucht beide Daten */}
                {kz.canRenew ? (
                  <div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
                      <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, fontWeight: 700, color: '#4b5563', textTransform: 'uppercase', letterSpacing: 0.4 }}>
                        Gültig ab
                        <input data-testid={`konz-issued-${v.id}`} type="date" value={kd.issue_date}
                          onChange={e => setKonzDate(v, 'issue_date', e.target.value)}
                          style={{ padding: '8px 10px', border: '1.5px solid #e5e7eb', borderRadius: 8, fontSize: 13, boxSizing: 'border-box' }} />
                      </label>
                      <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, fontWeight: 700, color: '#4b5563', textTransform: 'uppercase', letterSpacing: 0.4 }}>
                        Gültig bis
                        <input data-testid={`konz-expires-${v.id}`} type="date" value={kd.expires_at}
                          onChange={e => setKonzDate(v, 'expires_at', e.target.value)}
                          style={{ padding: '8px 10px', border: '1.5px solid #e5e7eb', borderRadius: 8, fontSize: 13, boxSizing: 'border-box' }} />
                      </label>
                    </div>
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 12px', background: '#f3f4f6', borderRadius: 8, cursor: kDatesOk ? 'pointer' : 'not-allowed', opacity: kDatesOk ? 1 : 0.55, border: '1px dashed #d1d5db' }}>
                      {kz.state === 'missing' ? <Upload size={13} color="#4b5563" /> : <RefreshCw size={13} color="#4b5563" />}
                      <span style={{ fontSize: 12, fontWeight: 600, color: '#4b5563' }}>{konzUploadLabel(kz.state)}</span>
                      <input data-testid={`konz-upload-${v.id}`} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp"
                        disabled={!kDatesOk} onChange={e => uploadKonzession(v, e)} style={{ display: 'none' }} />
                    </label>
                    {!kDatesOk && (
                      <div style={{ marginTop: 6, fontSize: 12, color: '#6b7280' }}>Bitte Ausstellungs- UND Ablaufdatum angeben, dann hochladen.</div>
                    )}
                  </div>
                ) : (
                  <div style={{ fontSize: 12, color: '#4b5563' }}>Einreichung wird geprüft.</div>
                )}
              </div>

              {/* Zulassungsbescheinigung Teil I — gleicher Reviewweg wie die Konzession,
                  fachlich aber ohne Ablaufdatum und ohne UI-Behauptung einer aktiven Fahrsperre. */}
              <div style={{ borderTop: '1px solid #f3f4f6', paddingTop: 12, marginBottom: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#4b5563', textTransform: 'uppercase', letterSpacing: 0.5 }}>Zulassungsbescheinigung Teil I (Fahrzeugschein)</div>
                  <span data-testid={`zulassung-status-${v.id}`}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 700, color: zs.presentation.color, background: zs.presentation.bg, padding: '3px 9px', borderRadius: 20 }}>
                    {konzIcon(zs.state)} {zs.presentation.label}
                  </span>
                </div>

                {zs.effective && (
                  <div style={{ fontSize: 12, color: '#4b5563', marginBottom: zs.renewalOpen ? 4 : 8 }}>
                    Ausgestellt am {fmtDate(zs.effective.issued_at || zs.effective.issue_date || zs.issuedAt)}
                  </div>
                )}
                {zs.renewalOpen && (
                  <div style={{ fontSize: 12, color: '#4b5563', marginBottom: 8 }}>Aktualisierung eingereicht — wird geprüft.</div>
                )}
                {zs.rejectionReason && (
                  <div style={{ fontSize: 12, color: '#dc2626', fontWeight: 600, marginBottom: 8 }}>Grund: {zs.rejectionReason}</div>
                )}
                {zs.state === 'compliance_locked' && (
                  <div style={{ fontSize: 12, color: '#991b1b', fontWeight: 600, marginBottom: 8 }}>Dieser Dokument-Slot ist administrativ gesperrt. Bitte wende dich an den Support.</div>
                )}

                {zs.canRenew ? (
                  <div>
                    <label style={{ display: 'flex', flexDirection: 'column', gap: 4, maxWidth: 220, marginBottom: 8, fontSize: 12, fontWeight: 700, color: '#4b5563', textTransform: 'uppercase', letterSpacing: 0.4 }}>
                      Ausstellungsdatum
                      <input data-testid={`zulassung-issued-${v.id}`} type="date" value={zd.issue_date}
                        onChange={e => setZulassungIssue(v, e.target.value)}
                        style={{ padding: '8px 10px', border: '1.5px solid #e5e7eb', borderRadius: 8, fontSize: 13, boxSizing: 'border-box' }} />
                    </label>
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 12px', background: '#f3f4f6', borderRadius: 8, cursor: zDateOk ? 'pointer' : 'not-allowed', opacity: zDateOk ? 1 : 0.55, border: '1px dashed #d1d5db' }}>
                      {zs.state === 'missing' ? <Upload size={13} color="#4b5563" /> : <RefreshCw size={13} color="#4b5563" />}
                      <span style={{ fontSize: 12, fontWeight: 600, color: '#4b5563' }}>{zulassungUploadLabel(zs.state)}</span>
                      <input data-testid={`zulassung-upload-${v.id}`} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp"
                        disabled={!zDateOk} onChange={e => uploadZulassung(v, e)} style={{ display: 'none' }} />
                    </label>
                    {!zDateOk && (
                      <div style={{ marginTop: 6, fontSize: 12, color: '#6b7280' }}>Bitte das Ausstellungsdatum angeben, dann hochladen. Ein Ablaufdatum gibt es nicht.</div>
                    )}
                  </div>
                ) : (
                  <div style={{ fontSize: 12, color: '#4b5563' }}>{zs.state === 'compliance_locked' ? 'Upload durch administrative Sperre blockiert.' : 'Einreichung wird geprüft.'}</div>
                )}
              </div>

              <div style={{ borderTop: '1px solid #f3f4f6', paddingTop: 12, marginTop: 12 }}>
                {removingVehicle === v.id ? (
                  <div data-testid={`remove-confirm-${v.id}`}
                    style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '10px 12px' }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#991b1b', marginBottom: 4 }}>Fahrzeug {v.license_plate} wirklich entfernen?</div>
                    <div style={{ fontSize: 12, color: '#7f1d1d', lineHeight: 1.45, marginBottom: 10 }}>
                      Fahrer verlieren die Auswahlberechtigung für dieses Fahrzeug. Nur wer dieses Fahrzeug aktiv nutzt, wird davon gelöst und offline gesetzt; ein anderes aktives Fahrzeug bleibt aktiv. Das Kennzeichen ist danach wieder frei und kann neu angelegt werden. Belege und Auftragsbuch bleiben erhalten. Während einer laufenden Fahrt mit diesem Fahrzeug ist das Entfernen nicht möglich.
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button data-testid={`remove-yes-${v.id}`} onClick={() => removeVehicle(v)} disabled={removeBusy}
                        style={{ padding: '7px 12px', background: '#dc2626', color: '#fff', borderRadius: 6, fontSize: 12, fontWeight: 700, border: 'none', opacity: removeBusy ? 0.6 : 1 }}>
                        Ja, entfernen
                      </button>
                      <button data-testid={`remove-no-${v.id}`} onClick={() => setRemovingVehicle(null)} disabled={removeBusy}
                        style={{ padding: '7px 12px', background: '#fff', color: '#374151', borderRadius: 6, fontSize: 12, fontWeight: 700, border: '1px solid #e5e7eb' }}>
                        Abbrechen
                      </button>
                    </div>
                  </div>
                ) : (
                  <button data-testid={`remove-${v.id}`} onClick={() => setRemovingVehicle(v.id)}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 10px', background: '#fff', color: '#b91c1c', borderRadius: 6, fontSize: 12, fontWeight: 700, border: '1px solid #fecaca', cursor: 'pointer' }}>
                    <Trash2 size={13} /> Fahrzeug entfernen
                  </button>
                )}
              </div>
              </>}
              {!fileId && <VehicleAssignments vehicle={v} drivers={drivers} onChanged={load} />}

            </div>
          );
        })}
      </div>
    </div>
  );
}
